import { Ok, Result, Id } from "@/utils"
import { api_fetch, HttpError } from "./client"

export enum TaskStatus {
  Pending = "pending",
  Completed = "completed",
  Archived = "archived",
}

export enum TaskType {
  FileDispute = "file-dispute",
  PullBackup = "pull-backup",
  Followup = "followup",
  Validate = "validate",
  MapAccountingCode = "map-accounting-code",
  Expense = "expense",
  /** Retired — DB enum only; not assignable in UI */
  UpdateDcPo = "update-dc-po",
}

export interface TaskInfo {
  type: TaskType
  status: TaskStatus
  user_id: string // Assuming Uuid is a string
  created_at: string // Assuming DateTime is a string
  assigner_email: string
  assigner_display_name?: string
  id: string // Assuming Uuid is a string
  user_email: string
  user_display_name?: string
  backup_type?: string
  due_date?: string
  note?: string
  /** Number of automatic reminder emails sent for this task. Absent on older task JSON rows. */
  bump_count?: number | null
  last_bump_at?: string | null
}

/**
 * Whether a deduction's task summary is a still-pending task assigned to `user`. Matches on user id,
 * falling back to a case-insensitive email match for task JSON written before `user_id` was reliable.
 */
export function is_pending_task_for_user(
  task: Pick<TaskInfo, "status" | "user_id" | "user_email"> | null | undefined,
  user: { id: string; email: string }
): boolean {
  if (!task || task.status !== TaskStatus.Pending) return false
  if (task.user_id && task.user_id === user.id) return true
  return !!task.user_email && task.user_email.toLowerCase() === user.email.toLowerCase()
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const user = { id: "u1", email: "Brand@Example.com" }

  describe("is_pending_task_for_user", () => {
    it("matches the user's own pending task by id or email", () => {
      expect(is_pending_task_for_user({ status: TaskStatus.Pending, user_id: "u1", user_email: "x@y.com" }, user)).toBe(true)
      expect(is_pending_task_for_user({ status: TaskStatus.Pending, user_id: "", user_email: "brand@example.com" }, user)).toBe(true)
    })

    it("ignores completed tasks, other assignees and missing tasks", () => {
      expect(is_pending_task_for_user({ status: TaskStatus.Completed, user_id: "u1", user_email: "brand@example.com" }, user)).toBe(false)
      expect(is_pending_task_for_user({ status: TaskStatus.Pending, user_id: "u2", user_email: "other@example.com" }, user)).toBe(false)
      expect(is_pending_task_for_user(undefined, user)).toBe(false)
    })
  })
}

/** A row from the `task` table as serialized on the deduction detail payload (`tasks`). */
export interface DeductionTask {
  id: string
  org_id: string
  user_id: string
  user_display_name: string
  assigner_id: string
  assigner_display_name: string
  due_date?: string | null
  task_type: TaskType
  status: TaskStatus
  backup_type?: string | null
  note?: string | null
  is_time_sensitive?: boolean | null
  deduction_id?: string | null
  dispute_id?: string | null
  created_at: string
  updated_at: string
  completed_at?: string | null
  last_bump_at?: string | null
  bump_count: number
}

export async function complete_task(task_id: Id): Promise<Result<TaskInfo, HttpError>> {
  const res = await api_fetch<TaskInfo>(`/task`, {
    method: "PUT",
    body: {
      status: TaskStatus.Completed,
      task_id,
    },
  })
  if (res.ok) {
    return Ok(res.value.data)
  } else {
    return res
  }
}

export interface TaskSubmission {
  deduction_id: Id
  user_id: Id
  task_type: TaskType
  backup_type: string
  note: string
  is_time_sensitive: boolean
}

export async function assign_tasks(submission: TaskSubmission[]): Promise<Result<DeductionTask[], HttpError>> {
  const res = await api_fetch<DeductionTask[]>(`/tasks`, {
    body: submission,
  })
  if (res.ok) {
    return Ok(res.value.data)
  } else {
    return res
  }
}

interface BulkCompleteTasksResponse {
  completed_count: number
}

export async function bulk_complete_tasks(deduction_ids: Id[]): Promise<Result<BulkCompleteTasksResponse, HttpError>> {
  const res = await api_fetch<BulkCompleteTasksResponse>(`/tasks/complete/bulk`, {
    body: { deduction_ids },
  })
  if (res.ok) {
    return Ok(res.value.data)
  } else {
    return res
  }
}
